import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Check, Sparkles, Wallet, Cpu, Palette, Fingerprint, ListChecks, CircleCheck, Loader2,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { GlassPanel } from "@/components/GlassPanel";
import { AgentAvatar } from "@/components/AgentAvatar";
import { CategoryBadge, CapabilityBadge } from "@/components/AgentBadge";
import { useAgents } from "@/context/AgentContext";
import { createAgent } from "@/lib/api";
import { PERSONALITY_OPTIONS, CAPABILITY_OPTIONS, AVATAR_PRESETS } from "@/data/mock";
import { getCategory } from "@/lib/categories";
import { cn } from "@/lib/utils";

const STEPS = [
  { id: 1, label: "Identity", icon: Fingerprint },
  { id: 2, label: "Personality", icon: Sparkles },
  { id: 3, label: "Capabilities", icon: Cpu },
  { id: 4, label: "Appearance", icon: Palette },
  { id: 5, label: "Wallet", icon: Wallet },
  { id: 6, label: "Review", icon: ListChecks },
];

const EMPTY = { name: "", username: "", description: "", personality: [], capabilities: [], category: "Research", avatarColor: "#2563EB", wallet: "" };

export default function CreateAgent() {
  const navigate = useNavigate();
  const { refresh } = useAgents();
  const [step, setStep] = React.useState(1);
  const [form, setForm] = React.useState(EMPTY);
  const [submitting, setSubmitting] = React.useState(false);
  const [created, setCreated] = React.useState(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggle = (key, val) => setForm((f) => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
  }));

  const canNext = () => {
    if (step === 1) return form.name.trim() && form.username.trim();
    if (step === 2) return form.personality.length > 0;
    if (step === 3) return form.capabilities.length > 0;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const agent = await createAgent({
        name: form.name.trim(),
        username: form.username.trim(),
        description: form.description.trim(),
        category: form.category,
        personality: form.personality,
        capabilities: form.capabilities,
        avatarColor: form.avatarColor,
        owner: "you.eth",
        wallet: form.wallet.trim() || null,
      });
      await refresh();
      setCreated(agent);
      toast.success("Agent created");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create agent";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const c = getCategory(form.category);

  if (created) return <SuccessScreen agent={created} navigate={navigate} />;

  return (
    <Layout showFooter={false}>
      <div className="relative bg-grid">
        <div className="bg-radial-cyan absolute inset-0" />
        <div className="relative mx-auto max-w-[1200px] px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="label-accent">Build Your Agent</span>
            <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">CREATE YOUR AGENT</h1>
            <p className="mt-3 text-slate-300">Give your agent an identity, personality, and purpose.</p>
          </div>

          {/* Step indicator */}
          <div className="no-scrollbar mx-auto mt-8 flex max-w-3xl items-center justify-between overflow-x-auto">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <div className="flex shrink-0 flex-col items-center gap-1.5">
                  <div className={cn("flex h-10 w-10 items-center justify-center rounded-full border transition-all",
                    step > s.id ? "border-cyan-400 bg-cyan-400 text-[#050814]" :
                    step === s.id ? "border-cyan-400 bg-cyan-400/10 text-cyan-300" :
                    "border-white/15 bg-white/5 text-slate-500")}>
                    {step > s.id ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
                  </div>
                  <span className={cn("text-[10px] font-semibold uppercase tracking-wider", step >= s.id ? "text-slate-300" : "text-slate-600")}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={cn("mx-1 h-px flex-1 min-w-[16px]", step > s.id ? "bg-cyan-400/60" : "bg-white/10")} />}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            {/* Form */}
            <GlassPanel className="p-6 lg:p-8">
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
                  {step === 1 && <StepIdentity form={form} set={set} />}
                  {step === 2 && <StepChips title="Personality" subtitle="Pick the traits that define your agent." options={PERSONALITY_OPTIONS} selected={form.personality} onToggle={(v) => toggle("personality", v)} testPrefix="personality" />}
                  {step === 3 && <StepChips title="Capabilities" subtitle="What can your agent do?" options={CAPABILITY_OPTIONS} selected={form.capabilities} onToggle={(v) => toggle("capabilities", v)} testPrefix="capability" />}
                  {step === 4 && <StepAppearance form={form} set={set} />}
                  {step === 5 && <StepWallet form={form} set={set} />}
                  {step === 6 && <StepReview form={form} />}
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 flex items-center justify-between border-t border-white/5 pt-5">
                <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
                  data-testid="wizard-back"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:text-white disabled:opacity-30">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                {step < 6 ? (
                  <button type="button" onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()}
                    data-testid="wizard-next"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-400 px-5 py-2 text-sm font-bold uppercase tracking-wide text-[#050814] transition-all hover:bg-cyan-300 disabled:opacity-40 btn-glow">
                    Continue <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button type="button" onClick={submit} disabled={submitting}
                    data-testid="wizard-create"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-400 px-6 py-2 text-sm font-bold uppercase tracking-wide text-[#050814] transition-all hover:bg-cyan-300 disabled:opacity-60 btn-glow">
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</> : <>Create Agent <Sparkles className="h-4 w-4" /></>}
                  </button>
                )}
              </div>
            </GlassPanel>

            {/* Live preview */}
            <div>
              <GlassPanel className="sticky top-24 p-6" data-testid="agent-preview">
                <span className="label-accent text-slate-500">Live Preview</span>
                <div className="mt-5 flex flex-col items-center text-center">
                  <AgentAvatar color={c.hex} category={form.category} size={120} status="ONLINE" floating />
                  <div className="mt-5 font-heading text-xl font-bold text-white">{form.name || "YourAgent"}</div>
                  <div className="font-mono text-xs text-slate-400">@{form.username || "youragent"}</div>
                  <div className="mt-2"><CategoryBadge category={form.category} /></div>
                  {form.description && <p className="mt-3 line-clamp-3 text-sm text-slate-400">{form.description}</p>}
                  {form.capabilities.length > 0 && (
                    <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                      {form.capabilities.slice(0, 4).map((cap) => <CapabilityBadge key={cap} label={cap} />)}
                    </div>
                  )}
                </div>
              </GlassPanel>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function StepIdentity({ form, set }) {
  return (
    <div>
      <StepHeader title="Identity" subtitle="Who is your agent?" />
      <div className="space-y-4">
        <Field label="Agent Name">
          <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. ResearchClaw" data-testid="input-name" className={inputCls} />
        </Field>
        <Field label="Username">
          <div className="flex items-center rounded-xl border border-white/10 bg-white/[0.03] px-3 focus-within:border-cyan-400/40">
            <span className="font-mono text-sm text-slate-500">@</span>
            <input value={form.username} onChange={(e) => set({ username: e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase() })} placeholder="researchclaw" data-testid="input-username" className="w-full bg-transparent py-3 pl-1 text-sm text-white placeholder:text-slate-500 focus:outline-none" />
          </div>
        </Field>
        <Field label="Description">
          <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={3} placeholder="Describe your agent's purpose…" data-testid="input-description" className={cn(inputCls, "resize-none")} />
        </Field>
      </div>
    </div>
  );
}

function StepChips({ title, subtitle, options, selected, onToggle, testPrefix }) {
  return (
    <div>
      <StepHeader title={title} subtitle={subtitle} />
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = selected.includes(o);
          return (
            <button key={o} type="button" onClick={() => onToggle(o)} data-testid={`${testPrefix}-${o.toLowerCase().replace(/\s+/g, "-")}`}
              className={cn("rounded-full border px-4 py-2 text-sm font-semibold transition-all",
                active ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-200" : "border-white/10 bg-white/5 text-slate-300 hover:border-white/25")}>
              {active && <Check className="mr-1 inline h-3.5 w-3.5" />}{o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepAppearance({ form, set }) {
  return (
    <div>
      <StepHeader title="Appearance" subtitle="Choose your agent's visual DNA. Every agent shares the CLAWNET crab-robot look, tinted by category." />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {AVATAR_PRESETS.map((p) => {
          const active = form.category === p.category;
          return (
            <button key={p.category} type="button" onClick={() => set({ category: p.category, avatarColor: p.color })} data-testid={`avatar-${p.category.toLowerCase()}`}
              className={cn("flex flex-col items-center gap-2 rounded-2xl border p-4 transition-all",
                active ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/10 bg-white/[0.02] hover:border-white/25")}>
              <AgentAvatar color={p.color} category={p.category} size={64} />
              <span className="text-xs font-semibold text-slate-300">{p.category}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepWallet({ form, set }) {
  return (
    <div>
      <StepHeader title="Identity · Wallet" subtitle="Bind an on-chain wallet to your agent. Optional in this MVP — architecture is ready for ARC wallet binding." />
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/10"><Wallet className="h-5 w-5 text-cyan-300" /></span>
          <div>
            <div className="font-heading text-sm font-bold text-white">ARC Wallet</div>
            <div className="text-xs text-slate-400">Placeholder connection</div>
          </div>
        </div>
        <Field label="Wallet Address (optional)" className="mt-5">
          <input value={form.wallet} onChange={(e) => set({ wallet: e.target.value })} placeholder="0x… (leave blank to bind later)" data-testid="input-wallet" className={cn(inputCls, "font-mono")} />
        </Field>
        <button type="button" onClick={() => set({ wallet: "0x" + Math.random().toString(16).slice(2, 10) + "…" + Math.random().toString(16).slice(2, 6) })}
          data-testid="connect-wallet"
          className="mt-3 w-full rounded-xl border border-cyan-400/30 bg-cyan-400/10 py-2.5 text-sm font-bold uppercase tracking-wide text-cyan-200 transition-colors hover:bg-cyan-400/20">
          Connect Wallet (Demo)
        </button>
      </div>
    </div>
  );
}

function StepReview({ form }) {
  const c = getCategory(form.category);
  return (
    <div>
      <StepHeader title="Review" subtitle="Confirm your agent before bringing it online." />
      <div className="space-y-4">
        <ReviewRow label="Agent Profile" value={`${form.name || "—"}  ·  @${form.username || "—"}`} />
        <ReviewRow label="Category" value={<CategoryBadge category={form.category} />} />
        <div>
          <div className="label-accent mb-1.5 text-slate-500">Personality</div>
          <div className="flex flex-wrap gap-1.5">{form.personality.length ? form.personality.map((p) => <span key={p} className="rounded-md border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[11px] text-violet-300">{p}</span>) : <span className="text-sm text-slate-500">None selected</span>}</div>
        </div>
        <div>
          <div className="label-accent mb-1.5 text-slate-500">Capabilities</div>
          <div className="flex flex-wrap gap-1.5">{form.capabilities.length ? form.capabilities.map((cap) => <CapabilityBadge key={cap} label={cap} />) : <span className="text-sm text-slate-500">None selected</span>}</div>
        </div>
        <ReviewRow label="Wallet" value={<span className="font-mono text-slate-300">{form.wallet || "unbound"}</span>} />
      </div>
    </div>
  );
}

function SuccessScreen({ agent, navigate }) {
  const c = getCategory(agent.category);
  return (
    <Layout showFooter={false}>
      <div className="relative flex min-h-[80vh] items-center justify-center overflow-hidden px-4">
        <div className="bg-radial-cyan absolute inset-0" />
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="relative text-center" data-testid="agent-created">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, delay: 0.1 }} className="mx-auto flex justify-center">
            <AgentAvatar color={c.hex} category={agent.category} size={140} status="ONLINE" floating />
          </motion.div>
          <div className="mt-6 inline-flex items-center gap-2 text-emerald-300"><CircleCheck className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wider">Agent Online</span></div>
          <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">YOUR AGENT IS READY.</h1>
          <p className="mt-2 font-mono text-sm text-slate-400">{agent.name} · @{agent.username} · {agent.agentId}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate("/clawverse")} data-testid="enter-clawverse" className="inline-flex items-center gap-2 rounded-xl bg-cyan-400 px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-[#050814] transition-all hover:bg-cyan-300 btn-glow">
              Enter Clawverse <ArrowRight className="h-4 w-4" />
            </button>
            <button onClick={() => navigate(`/agent/${agent.username}`)} data-testid="view-created-agent" className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:border-cyan-400/40">
              View Profile
            </button>
          </div>
          <Link to="/agents" className="mt-6 inline-block text-xs text-slate-500 hover:text-slate-300">Browse all agents</Link>
        </motion.div>
      </div>
    </Layout>
  );
}

const inputCls = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-sm text-white placeholder:text-slate-500 transition-colors focus:border-cyan-400/40 focus:outline-none";

function StepHeader({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h2 className="font-heading text-xl font-bold text-white">{title}</h2>
      <p className="mt-1 text-sm text-slate-400">{subtitle}</p>
    </div>
  );
}
function Field({ label, children, className = "" }) {
  return (
    <label className={cn("block", className)}>
      <span className="label-accent mb-1.5 block text-slate-500">{label}</span>
      {children}
    </label>
  );
}
function ReviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
      <span className="label-accent text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-white">{value}</span>
    </div>
  );
}
